import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, type Column } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Toolbar } from '@/components/common/Toolbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { collectionsService } from '@/services/inventory';
import type { Collection } from '@/types/inventory';
import { useDebounced } from '@/hooks/useDebounced';
import { CollectionFormSheet } from './CollectionFormSheet';

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null;

export function CollectionsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Collection | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Collection | null>(null);
  const debounced = useDebounced(search);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['inventory', 'collections'],
    // includeInactive so the operator sees draft/seasonal collections too.
    queryFn: () => collectionsService.list({ includeInactive: true }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => collectionsService.remove(id),
    onSuccess: () => {
      toast.success('Collection deleted');
      qc.invalidateQueries({ queryKey: ['inventory', 'collections'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not delete collection'),
  });

  const filtered = useMemo(() => {
    const rows = data ?? [];
    const q = debounced.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        (c.description?.toLowerCase().includes(q) ?? false),
    );
  }, [data, debounced]);

  const columns: Column<Collection>[] = [
    {
      key: 'name',
      header: 'Collection',
      cell: (c) => (
        <div className="flex items-center gap-2.5">
          {c.imageUrl ? (
            <img
              src={c.imageUrl}
              alt=""
              loading="lazy"
              className="h-9 w-9 shrink-0 rounded-md border border-border/60 object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-dashed border-border/60 bg-muted/40 text-muted-foreground"
            >
              <Sparkles className="h-4 w-4" />
            </span>
          )}
          <div className="min-w-0 space-y-0.5">
            <div className="font-medium text-foreground">{c.name}</div>
            <div className="font-mono text-[11px] text-muted-foreground">/{c.slug}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '110px',
      cell: (c) =>
        c.status === 'ACTIVE' ? (
          <Badge variant="success" className="text-[10.5px]">Active</Badge>
        ) : (
          <Badge variant="muted" className="text-[10.5px]">Inactive</Badge>
        ),
    },
    {
      key: 'schedule',
      header: 'Schedule',
      width: '190px',
      cell: (c) => {
        const s = fmtDate(c.startsAt);
        const e = fmtDate(c.endsAt);
        if (!s && !e) return <span className="text-[12px] text-muted-foreground">Always</span>;
        return (
          <span className="text-[12px] text-foreground">
            {s ?? '—'} <span className="text-muted-foreground">→</span> {e ?? '—'}
          </span>
        );
      },
    },
    {
      key: 'products',
      header: 'Products',
      align: 'right',
      width: '90px',
      cell: (c) => (
        <span className="font-mono text-[12.5px] tabular-nums text-foreground">
          {c.itemCount ?? 0}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '110px',
      cell: (c) => (
        <div className="-mr-2 flex items-center justify-end gap-0.5">
          <Button variant="ghost" size="sm" onClick={() => setEditing(c)} aria-label={`Open ${c.name}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmRemove(c)}
            disabled={remove.isPending}
            aria-label={`Delete ${c.name}`}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Inventory"
        title="Collections"
        description="Curated marketing groups for merchandising (Gift, Diwali, “Under ₹999”). A product can be in many collections — separate from its category. Deliver these to the storefront to build shop-by-occasion edits."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            New collection
          </Button>
        }
      />

      <Toolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, slug, description…"
      />

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(c) => c.id}
          isLoading={isLoading}
          onRowClick={(c) => setEditing(c)}
          emptyTitle="No collections yet"
          emptyDescription="Create your first collection to group products for marketing — e.g. a Diwali edit or a gifting range."
          emptyAction={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Sparkles className="h-3.5 w-3.5" />
              New collection
            </Button>
          }
        />
      )}

      <CollectionFormSheet open={createOpen} onOpenChange={setCreateOpen} />
      <CollectionFormSheet
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        collection={editing}
      />
      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={confirmRemove ? `Delete collection "${confirmRemove.name}"?` : ''}
        description={
          <>
            The collection and its product memberships are permanently removed.
            The products themselves are not affected. This cannot be undone.
          </>
        }
        confirmLabel="Delete collection"
        tone="destructive"
        onConfirm={() => {
          if (confirmRemove) void remove.mutateAsync(confirmRemove.id);
        }}
      />
    </div>
  );
}
